import axios from "axios";

export const RPPG_CHUNK_DURATION_MS = 2000;
export const RPPG_QUICK_VITALS_START_CHUNK = 5;
export const RPPG_QUICK_VITALS_INTERVAL_MS = 5000;
export const RPPG_VITAL_PREDICT_CHUNK = 25;
export const RPPG_MAX_CHUNKS = 35;
export const RPPG_SCAN_DURATION_MS = RPPG_MAX_CHUNKS * RPPG_CHUNK_DURATION_MS;
export const RPPG_RESULT_POLL_INTERVAL_MS = 2500;
export const RPPG_RESULT_POLL_MAX_MS = 90_000;
export const RPPG_CHUNK_UPLOAD_MAX_RETRIES = 3;

export interface RppgUploadInitResponse {
  reading_id: string;
  bucket: string;
  key_prefix: string;
  key_template: string;
  expires_in: number;
  max_chunk_bytes: number;
  upload: {
    url: string;
    fields: Record<string, string>;
  };
}

export interface RppgDemographics {
  age?: number;
  gender?: string;
  height_cm?: number;
  weight_kg?: number;
  diabetes?: string;
}

export interface RppgUploadMetadata {
  schema_version: 1;
  client_name: string;
  device: {
    platform: "web";
    os: string;
    os_version: string;
    app_version?: string;
    browser?: string;
    browser_version?: string;
    model?: string;
    camera?: {
      facing: "front";
      requested_fps: number;
    };
  };
  location?: {
    granularity: "precise" | "coarse";
    source: "gps" | "ip";
    lat?: number | null;
    lng?: number | null;
    alt?: number | null;
    accuracy_m?: number | null;
  };
}

export interface RppgQuickVitals {
  heart_rate?: number;
  respiratory_rate?: number;
  oxygen_saturation?: number;
  stress?: number;
  spo2?: number;
  [key: string]: number | undefined;
}

export interface RppgFinalResult {
  reading_id?: string;
  predictions: Record<string, unknown>;
  quality?: Record<string, unknown>;
  [key: string]: unknown;
}

const OVERRIDDEN_PRESIGNED_FIELDS = new Set(["key", "Content-Type"]);

export class RppgGatewayTimeoutError extends Error {
  constructor() {
    super("vital-predict gateway timeout — fall back to polling");
    this.name = "RppgGatewayTimeoutError";
  }
}

function buildRppgClient(apiKey: string, baseUrl: string) {
  return axios.create({
    baseURL: baseUrl.replace(/\/$/, ""),
    headers: {
      "x-api-key": apiKey,
      "Content-Type": "application/json",
    },
    timeout: 35_000,
  });
}

function isRppgProcessingResponse(data: unknown): boolean {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as { status?: string }).status === "processing"
  );
}

export function isRppgResultReady(data: unknown): data is RppgFinalResult {
  if (!data || typeof data !== "object" || isRppgProcessingResponse(data)) {
    return false;
  }
  return Boolean((data as RppgFinalResult).predictions);
}

function shouldPollAfterVitalPredictError(status: number | undefined): boolean {
  return (
    status === 502 ||
    status === 504 ||
    (typeof status === "number" && status >= 500)
  );
}

export async function rppgUploadInit(
  apiKey: string,
  baseUrl: string,
  readingId?: string,
  metadata?: RppgUploadMetadata,
): Promise<RppgUploadInitResponse> {
  const client = buildRppgClient(apiKey, baseUrl);
  const body: Record<string, unknown> = {};
  if (readingId) {
    body.reading_id = readingId;
  }
  if (metadata) {
    body.metadata = metadata;
  }
  const response = await client.post<RppgUploadInitResponse>(
    "/v1/upload-init",
    body,
  );
  return response.data;
}

export async function rppgUploadChunk(
  uploadUrl: string,
  fields: Record<string, string>,
  keyPrefix: string,
  chunkIndex: number,
  chunkBlob: Blob,
): Promise<number> {
  const form = new FormData();

  for (const [k, v] of Object.entries(fields)) {
    if (OVERRIDDEN_PRESIGNED_FIELDS.has(k)) {
      continue;
    }
    form.append(k, v);
  }

  const chunkKey = `${keyPrefix}chunk_${chunkIndex}/video.mp4`;
  form.append("key", chunkKey);
  form.append("Content-Type", "video/mp4");

  const uploadBlob =
    chunkBlob.type === "video/mp4"
      ? chunkBlob
      : new Blob([chunkBlob], { type: "video/mp4" });
  form.append("file", uploadBlob, `chunk_${chunkIndex}.mp4`);

  const response = await fetch(uploadUrl, {
    method: "POST",
    body: form,
  });

  if (response.status !== 204 && response.status !== 201) {
    throw new Error(
      `Chunk ${chunkIndex} upload failed with status ${response.status}`,
    );
  }

  return response.status;
}

export async function rppgQuickVitals(
  apiKey: string,
  baseUrl: string,
  readingId: string,
  nChunks: number,
): Promise<RppgQuickVitals | null> {
  const client = buildRppgClient(apiKey, baseUrl);
  try {
    const response = await client.post<{
      predictions: RppgQuickVitals;
      status?: string;
    }>(
      "/v1/quick-vitals",
      { reading_id: readingId, n_chunks: nChunks },
      { validateStatus: (status) => status === 200 || status === 202 },
    );
    if (response.status === 202 || isRppgProcessingResponse(response.data)) {
      return null;
    }
    return response.data?.predictions ?? null;
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    console.warn("[rPPG] quick-vitals error:", message, {
      reading_id: readingId,
      n_chunks: nChunks,
    });
    return null;
  }
}

export async function rppgVitalPredict(
  apiKey: string,
  baseUrl: string,
  readingId: string,
  demographics: RppgDemographics,
  nChunks: number = RPPG_VITAL_PREDICT_CHUNK,
): Promise<RppgFinalResult> {
  const client = buildRppgClient(apiKey, baseUrl);
  const requestBody = {
    reading_id: readingId,
    demographics,
    n_chunks: nChunks,
  };

  const postPredict = () =>
    client.post<RppgFinalResult>("/v1/vital-predict", requestBody);

  try {
    const response = await postPredict();
    return response.data;
  } catch (err: unknown) {
    const status = axios.isAxiosError(err) ? err.response?.status : undefined;
    if (shouldPollAfterVitalPredictError(status)) {
      try {
        const retryResponse = await postPredict();
        return retryResponse.data;
      } catch (retryErr: unknown) {
        const retryStatus = axios.isAxiosError(retryErr)
          ? retryErr.response?.status
          : undefined;
        if (shouldPollAfterVitalPredictError(retryStatus)) {
          throw new RppgGatewayTimeoutError();
        }
        throw retryErr;
      }
    }
    throw err;
  }
}

export async function rppgGetResult(
  apiKey: string,
  baseUrl: string,
  readingId: string,
): Promise<RppgFinalResult | null> {
  const client = buildRppgClient(apiKey, baseUrl);
  const response = await client.get<RppgFinalResult & { status?: string }>(
    "/v1/result",
    {
      params: { rid: readingId, variant: "fast" },
      validateStatus: (status) => status === 200 || status === 202,
    },
  );

  if (response.status === 202 || !isRppgResultReady(response.data)) {
    return null;
  }

  return response.data;
}

export async function rppgPollFastResult(
  apiKey: string,
  baseUrl: string,
  readingId: string,
): Promise<RppgFinalResult | null> {
  const deadline = Date.now() + RPPG_RESULT_POLL_MAX_MS;

  while (Date.now() < deadline) {
    const result = await rppgGetResult(apiKey, baseUrl, readingId);
    if (result !== null) {
      return result;
    }
    await new Promise<void>((resolve) =>
      setTimeout(resolve, RPPG_RESULT_POLL_INTERVAL_MS),
    );
  }

  return null;
}

export async function rppgResolveFinalResult(
  apiKey: string,
  baseUrl: string,
  readingId: string,
  demographics: RppgDemographics,
  nChunks: number = RPPG_VITAL_PREDICT_CHUNK,
): Promise<RppgFinalResult | null> {
  try {
    const result = await rppgVitalPredict(
      apiKey,
      baseUrl,
      readingId,
      demographics,
      nChunks,
    );
    if (isRppgResultReady(result)) {
      return result;
    }
  } catch (err) {
    if (!(err instanceof RppgGatewayTimeoutError)) {
      console.warn("[rPPG] vital-predict error, falling back to poll:", err);
    }
  }

  return rppgPollFastResult(apiKey, baseUrl, readingId);
}

export async function uploadChunkWithRetry(
  uploadUrl: string,
  fields: Record<string, string>,
  keyPrefix: string,
  chunkIndex: number,
  chunkBlob: Blob,
  maxRetries = RPPG_CHUNK_UPLOAD_MAX_RETRIES,
): Promise<boolean> {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      await rppgUploadChunk(
        uploadUrl,
        fields,
        keyPrefix,
        chunkIndex,
        chunkBlob,
      );
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      console.warn(
        `[rPPG] chunk ${chunkIndex} upload attempt ${attempt + 1}/${maxRetries} failed:`,
        message,
      );
    }
  }
  return false;
}
